import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { AdminRecipientStep, AdminReviewCard } from '../../../../api/types'
import { useCreateAdminRecipientStep, useUpdateAdminRecipientStep } from '../../../../api/recipient-steps'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'

interface RecipientStepValues {
  displayName: string
  description: string
  continueButtonText: string
  order: number
  showChangeRequest: boolean
  changeRequestReviewCardReferenceID: string | null
}

const recipientStepSchema = yup.object({
  displayName: yup.string().required('Enter a display name'),
  description: yup.string().optional().default(''),
  continueButtonText: yup.string().optional().default(''),
  order: yup.number().typeError('Enter a valid number').min(0, 'Order must be 0 or more').required('Enter an order'),
  showChangeRequest: yup.boolean().default(false),
  changeRequestReviewCardReferenceID: yup.string().nullable().default(null),
})

interface RecipientStepDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The step being edited, or null to create a new one. */
  step: AdminRecipientStep | null
  /** All existing steps on the version — used to prevent duplicate order values. */
  existingSteps: AdminRecipientStep[]
  /** Default order for a new step (appended to the end). */
  nextOrder: number
  /** Review cards on the agent side — a change-request step can map back to one of these. */
  reviewCards: AdminReviewCard[]
  onSaved: () => void
}

/** Add/Edit drawer for a recipient step. Creates (POST) or updates (PUT) then triggers a builder refresh. */
export const RecipientStepDrawer: React.FC<RecipientStepDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  step,
  existingSteps,
  nextOrder,
  reviewCards,
  onSaved,
}) => {
  const formId = 'recipient-step-form'
  const [createStep, creating] = useCreateAdminRecipientStep(formReferenceID, versionReferenceID)
  const [updateStep, updating] = useUpdateAdminRecipientStep(
    formReferenceID,
    versionReferenceID,
    step?.referenceID ?? '',
  )
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<RecipientStepValues>({
    resolver: yupResolver(recipientStepSchema),
    defaultValues: {
      displayName: '',
      description: '',
      continueButtonText: '',
      order: nextOrder,
      showChangeRequest: false,
      changeRequestReviewCardReferenceID: null,
    },
  })

  // Reset the form to the step's values (or blank create defaults) each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    reset({
      displayName: step?.displayName ?? '',
      description: step?.description ?? '',
      continueButtonText: step?.continueButtonText ?? '',
      order: step?.order ?? nextOrder,
      showChangeRequest: step?.showChangeRequest ?? false,
      changeRequestReviewCardReferenceID: step?.changeRequestReviewCardReferenceID ?? null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step])

  const handleClose = () => onOpenChange(false)

  const showChangeRequest = Boolean(watch('showChangeRequest'))
  const changeRequestReviewCardReferenceID = watch('changeRequestReviewCardReferenceID')
  const changeRequestCardOption =
    reviewCards.find((card) => card.referenceID === changeRequestReviewCardReferenceID) ?? null

  const onSubmit: SubmitHandler<RecipientStepValues> = async (values) => {
    // Order must be unique across the version's steps (excluding the step being edited).
    const takenOrders = existingSteps
      .filter((existing) => existing.referenceID !== step?.referenceID)
      .map((existing) => existing.order)
    if (takenOrders.includes(values.order)) {
      setError('order', {
        type: 'manual',
        message: `A recipient step already exists with order ${values.order}. Choose a different order.`,
      })
      return
    }

    try {
      if (step) await updateStep(values)
      else await createStep(values)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={step ? 'Edit Recipient Step' : 'Add Recipient Step'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <InputGroup
            label="Display Name *"
            placeholder="e.g. Lease Details"
            {...register('displayName')}
            errorMessage={errors.displayName?.message}
          />
          <InputGroup
            label="Description"
            placeholder="Optional description shown on the step"
            {...register('description')}
            errorMessage={errors.description?.message}
          />
          <div>
            <InputGroup
              label="Button Text"
              placeholder="Continue"
              {...register('continueButtonText')}
              errorMessage={errors.continueButtonText?.message}
            />
            <FieldHelp>
              The text shown on this step&apos;s primary action button in the recipient app, e.g. Continue, Sign, or
              Submit.
            </FieldHelp>
          </div>
          <InputGroup
            label="Order *"
            inputMode="numeric"
            {...register('order', { valueAsNumber: true })}
            errorMessage={errors.order?.message}
          />
          <Checkbox
            label="Allow Change Request"
            supplementaryInfo="When selected, the recipient can request a change that routes back to an agent review card."
            checked={showChangeRequest}
            onChange={() => {
              const next = !showChangeRequest
              setValue('showChangeRequest', next)
              if (!next) setValue('changeRequestReviewCardReferenceID', null)
            }}
          />
          {showChangeRequest && (
            <Select
              label="Change Request Review Card"
              isClearable
              // Without these, the isClearable placeholder option defaults to label/value keys, which
              // AdminReviewCard doesn't have, so it falls through getOptionLabel's fallback and renders as
              // a phantom "Untitled card" option (same issue as the PDF Template picker).
              optionLabelKey="displayName"
              optionValueKey="referenceID"
              options={reviewCards}
              value={changeRequestCardOption}
              getOptionLabel={(option: AdminReviewCard) => option.displayName || 'Untitled card'}
              getOptionValue={(option: AdminReviewCard) => option.referenceID}
              onChange={(option: AdminReviewCard | null) =>
                setValue('changeRequestReviewCardReferenceID', option?.referenceID ?? null)
              }
              errorMessage={(errors.changeRequestReviewCardReferenceID as { message?: string })?.message}
            />
          )}
        </FieldStack>
      )}
    </Drawer>
  )
}
